import { myToolbar } from '../tools/toolbar.js';
import { mirror } from '../tools/mirror.js';
import { beads, Bead } from '../beads.js';
import { settings } from '../settings.js';
import { applyStar } from './applyStar.js';
import { applySquare } from './applySquare.js';

export const star = {
    currentView: 'F',
    tempArray: []
};

star.changeViewToSquare = viewName => {

    if (star.currentView === 'F' || (star.currentView === 'B')) {
        star.morphToSquare();
    }

    beads.arr.forEach((bead, i) => {
        beads.unselect(bead);
        if (!bead.star) return;
        bead.hidden = !(bead.star.square === viewName);
    });
};

star.changeViewToStar = viewName => {

    myToolbar.disableTool('Bead Mode');

    // deal with mirror
    star.mirrorPoint = {
        x: 120 + ((settings.rows - 5) * 22) + 400 + (settings.rows * 22),
        y: 162 + ((settings.rows - 5) * 30) + 400 + (settings.rows * 18)
    };

    if (beads.mirror) {
        beads.mirror.forEach(mirror => {
            mirror.setX(star.mirrorPoint.x);
            mirror.setY(star.mirrorPoint.y);
        });
        mirror.remove();
    }

    const rowLengths = new Array(settings.rows).fill('').map((e, i) => i + 1);

    beads.arr.forEach((bead, i) => {
        applyStar({bead, i, viewName, rowLengths});
        beads.unselect(bead);
    });
};

b3.copyFrontToBack = () => {
    beads.arr.forEach(bead => {
        if (!bead.star) return;
        if (bead.star.side > 2) return;
        const backBead = beads.arr.find(_bead =>
            _bead.star?.side === bead.star.side + 2 &&
            _bead.star?.beadNumber === bead.star.beadNumber &&
            _bead.star?.square === bead.star.square &&
            _bead.star?.row === bead.star.row
        )
        if (!backBead) return;
        backBead.style = bead.style;
        backBead.active = bead.active;
    });
};

star.changeView = viewName => {

    if ([1, 2, 3, 4, 5].some(element => element === viewName)) {
        star.changeViewToSquare(viewName);
    } else {
        star.changeViewToStar(viewName);
    }

    beads.refreshCanvas();
    star.currentView = viewName;
};

star.alterProject = () => {
    beads.tempArray = beads.arr
        .filter(bead => !bead.ignore)
        .map(bead => ({
            star: bead.star,
            style: bead.style
        }));
};

star.morphToSquare = () => {

    star.mirrorPoint = {
        x: 400 + (settings.rows * 21),
        y: 400 + (settings.rows * 21)
    };

    if (beads.mirror && settings.pattern.name === 'star') {
        beads.mirror.forEach(mirror => {
            mirror.setX(star.mirrorPoint.x);
            mirror.setY(star.mirrorPoint.y);
        });
        mirror.remove();
    }

    const rowLengths = new Array(settings.rows).fill('').map((e, i) => i + 1);

    beads.arr.forEach((bead, i) => applySquare({bead, i, rowLengths}));
};

export const starConfig = {
    name: 'star',
    displayName: 'Peyote Star',
    colDist: () => 38,
    rowDist: () => 32,
    offset: () => -19,
    offsetPattern: [0, 1],
    apply: () => {
        myToolbar.enableTool('Bead Mode');
        star.morphToSquare();
        star.changeView('F');
    },
    create: () => {
        const n = settings.rows;
        const beadsNeeded = (n * (n + 1)) * 10 + (10 * n);
        for (let i = 0; i < beadsNeeded; i++) {
            let j = Math.floor(i / 10);
            beads.arr.push(new Bead(j, i));
        }
    },
    commutes: false,
    canSelectCommute: false,
    plastic: false,
    canSelectDirection: false,
    canSelectStartingBead: false,
    canSelectPanelWidth: false,
    canExportSmallDiagrams: false,
    beadFromPoint: true,
    noCustomBeadSize: true,
    noColumns: true,
    rowLimit: 30,
    noChangePattern: true,
    noDrag: true,
    noGuides: true,
    getDimensions: (myRows, myColumns, diameter, beadLength) => {
        return {
            width: myRows * beadLength * 0.4,
            height: myRows * beadLength * 0.4
        };
    },
    getExportImageDimensions: rows => {
        return {
            width: 2500 * (Math.max(settings.rows, 7) / 15),
            height: 2500 * (Math.max(settings.rows, 7) / 15)
        }
    },
    getBeadingModeStartingPosition: ({bead}) => [1, null],
    disabledTools: ['Row Shift', 'Bead Mode', 'Paint'], // actually has beading mode, just not in star view
    getRowsAndColumns: ({rows, columns, size}) => {
        rows = size;
        columns = 0; // not used
        return [rows, columns];
    },
    useSingleRenderTexture: false, // changed
    useFourRenderTextures: true,
    manualFindBead: true,
    alterProject: star.alterProject,
    specialMirror: true,
    startMirror: () => {
        beads.mirror.forEach(mirror => {
            mirror.setX(star.mirrorPoint.x);
            mirror.setY(star.mirrorPoint.y);
        });

        if (!beads.mirror.active || (beads.mirror.orientation === 'none')) {
            beads.mirror.active = true;
            beads.mirror.orientation = 'vertical';

            if ([1, 2, 3, 4, 5].some(element => element === star.currentView)) {
                beads.mirror[1].setAngle(0).setAlpha(1);
                beads.mirror[0].setAngle(0).setAlpha(1);
            } else {
                beads.mirror[0].setAngle(0).setAlpha(1);
                beads.mirror[1].setAngle(0).setAlpha(0);
            }
        }
    },
    findMirrorBead: mirror.findStarMirrorBead,
    getRTLocation() {
        let x = 250 - ((settings.rows - 3) * 15);
        let y = 250 - ((settings.rows - 7) * 15);
        if (settings.rows < 8) x += 80;
        return [x, y];
    },
    getStripsKey: 'getStarStrips',
    getWordChartKey: 'getSquareWordChart',
    noDiagrams: true,
    getImportedBead: ({bead}) => {
        if (!bead.star) return;
        return beads.tempArray.find(element =>
            element.star?.beadNumber === bead.star.beadNumber &&
            element.star?.row === bead.star.row &&
            element.star?.side === bead.star.side &&
            element.star?.square === bead.star.square
        );
    },
    doBeadingKey: 'square',
    advanceBeadingKey: 'advanceSquare',
    rotateMirror: mirror.rotateSquareMirror,
    arrangeGrid: false
};